/**
 * Shared 429 backoff gate for the status/progress pollers.
 *
 * The backend sends **no** `Retry-After` on its 429s, and its `X-Rate-Limit-*`
 * headers do not survive onto error responses (the response envelope rebuilds
 * Boom errors from `output.payload`, dropping `output.headers`). So there is
 * nothing to read — the client has to guess, which is what this does.
 *
 * Deliberately **not** wired into `api()`. A gate down there would also stall
 * user-initiated requests, and a rate-limited poller is no reason to stop
 * responding to clicks. Only the pollers consult this.
 */

/** First pause after a 429. Short — most limits clear quickly. */
const INITIAL_BACKOFF_MS = 5_000;

/** Ceiling. Long enough to drain a sliding window, short enough to recover. */
const MAX_BACKOFF_MS = 60_000;

/** Up to this much random extra, so several tabs don't resume in lockstep. */
const MAX_JITTER_MS = 2_000;

let backoffUntil = 0;
let currentBackoffMs = 0;

/** True while pollers should stay quiet. Check before every polled request. */
export const isPollBackedOff = (): boolean => Date.now() < backoffUntil;

/**
 * Record a 429 from a polled request. Doubles the pause each consecutive time,
 * capped, with jitter.
 *
 * Jitter matters more than it looks: every tab of every open library polls on
 * the same fixed interval, and the rate-limit bucket is shared per (library,
 * user). Without jitter they all back off for the identical duration and then
 * retry simultaneously, re-tripping the limit — the classic thundering herd.
 */
export const notePollRateLimited = (): void => {
  currentBackoffMs =
    currentBackoffMs === 0 ? INITIAL_BACKOFF_MS : Math.min(currentBackoffMs * 2, MAX_BACKOFF_MS);
  backoffUntil = Date.now() + currentBackoffMs + Math.random() * MAX_JITTER_MS;
};

/**
 * Record a polled request that came back clean, resetting the escalation.
 *
 * Only call this on an actual success. Calling it on any settled request
 * (including errors) would reset the ladder on a network blip and defeat the
 * escalation entirely.
 */
export const notePollSucceeded = (): void => {
  currentBackoffMs = 0;
  backoffUntil = 0;
};
